import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ImageOutlined } from '@mui/icons-material';
import http from '../../services/http';

/**
 * A imagem anexada a uma pergunta do Orby.
 *
 * Antes ela não aparecia em lugar nenhum: era lida, mandada para a IA analisar
 * e descartada. O balão mostrava apenas a legenda "Imagem anexada".
 *
 * Duas fontes, nesta ordem:
 *
 *   1. a **prévia local** (data URL), que existe enquanto a aba não recarregou
 *      — é o que faz a imagem aparecer no mesmo instante do envio;
 *   2. o **caminho gravado no servidor**, buscado sob demanda.
 *
 * Por que buscar em vez de apontar o `src` direto: o endpoint exige JWT, e um
 * `<img src>` não manda cabeçalho nenhum. É o mesmo motivo pelo qual as imagens
 * embutidas nas ações de chamado são baixadas para blob antes de exibir. E o
 * caminho estático em `/storage` está bloqueado de propósito — conversa do Orby
 * é privada de quem conversou.
 */
const cache = new Map();

const buscarBlobUrl = async (path) => {
  if (cache.has(path)) return cache.get(path);

  const { data } = await http.get(`/ai-assistant/image?path=${encodeURIComponent(path)}`, {
    responseType: 'blob',
  });

  const url = URL.createObjectURL(data);
  cache.set(path, url);

  return url;
};

const OrbyImage = ({ preview, path, onAmpliar }) => {
  const [src, setSrc] = useState(preview || null);
  const [carregando, setCarregando] = useState(!preview && !!path);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    if (preview) {
      setSrc(preview);
      return undefined;
    }

    if (!path) return undefined;

    let cancelado = false;
    setCarregando(true);

    buscarBlobUrl(path)
      .then((url) => {
        if (!cancelado) setSrc(url);
      })
      .catch(() => {
        // A conversa pode ter sido aberta por quem não é o dono, ou o arquivo
        // ter sumido. Cai para o indicador discreto em vez de um ícone quebrado.
        if (!cancelado) setFalhou(true);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => { cancelado = true; };
  }, [preview, path]);

  if (carregando) {
    return (
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
        <CircularProgress size={14} color="inherit" />
        <Typography variant="caption">Carregando imagem…</Typography>
      </Box>
    );
  }

  if (!src || falhou) {
    return (
      <Typography
        variant="caption"
        sx={{ mt: 0.5, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        <ImageOutlined sx={{ fontSize: 14 }} />
        Imagem anexada
      </Typography>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt="Imagem anexada à pergunta"
      onClick={() => onAmpliar?.(src)}
      sx={{
        display: 'block',
        mt: 1,
        maxWidth: '100%',
        maxHeight: 260,
        borderRadius: 1,
        cursor: onAmpliar ? 'zoom-in' : 'default',
      }}
    />
  );
};

export default OrbyImage;
